import React, { useState } from 'react';
import { CRow, CCol, CInput, CLabel, CFormGroup, CInputGroup, CInputCheckbox } from '@coreui/react';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import _t from 'counterpart';
import toast from 'react-hot-toast';
import { useGetSetting } from './hooks';
import { extractErrorMessage, findErrorFromValidation, handleFormError } from '../../helpers';
import { useMutation } from 'react-query';
import { updateSmtpProvider } from '../../services/BackendService';
import { useAppSelector } from '../../helpers/customHooks';
import { Permission } from '../../reducers/userReducer';
import { shallowEqual } from 'react-redux';

interface IParams {
  sender: string;
  host: string;
  port: string;
  username: string;
  password: string;
  secure: boolean;
  staffEmail: string;
  dealingEmail: string;
  sensitiveStaffEmail?: string;
}

interface IProps {
  onChange: () => void;
}

const SmtpProviderIntegration = ({ onChange }: IProps) => {
  const [host, setHost] = useState<string>('');
  const [username, setUsername] = useState<string>('');
  const [port, setPort] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [emailSender, setEmailSender] = useState<string>('');
  const [secure, setSecure] = useState<boolean>(false);
  const [staffEmail, setStaffEmail] = useState<string>('');
  const [sensitiveStaffEmail, setSensitiveStaffEmail] = useState<string>('');
  const [dealingEmail, setDealingEmail] = useState<string>('');

  const userPermissions = useAppSelector((state) => state.user.permissions, shallowEqual);
  const canManageSensitiveEmail = userPermissions.includes(Permission.MANAGE_CUSTOMER_SECURITY_LEVEL);
  const hostQuery = useGetSetting('SMTP_HOST', 'smtp-host', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setHost(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const emailSenderQuery = useGetSetting('EMAIL_TRANSACTIONAL_SENDER', 'email-sender', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setEmailSender(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const portQuery = useGetSetting('SMTP_PORT', 'smtp-port', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setPort(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const usernameQuery = useGetSetting('SMTP_USERNAME', 'smtp-username', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setUsername(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const passwordQuery = useGetSetting('SMTP_PASSWORD', 'smtp-password', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setPassword(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  useGetSetting('SMTP_SECURE', 'smtp-secure', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: boolean) => {
      setSecure(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const staffEmailQuery = useGetSetting('STAFF_NOTIFICATION_EMAIL', 'staff-email', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setStaffEmail(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const sensitiveStaffEmailQuery = useGetSetting('STAFF_NOTIFICATION_EMAIL_SENSITIVE', 'sensitive-staff-email', {
    retry: false,
    refetchOnWindowFocus: false,
    enabled: canManageSensitiveEmail,
    onSuccess: (value: string) => {
      setSensitiveStaffEmail(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const dealingEmailQuery = useGetSetting('DEALING_NOTIFICATION_EMAIL', 'dealing-email', {
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (value: string) => {
      setDealingEmail(value);
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const updateSmtpProviderMutation = useMutation(
    ({ sender, host, port, username, password, secure, staffEmail, dealingEmail, sensitiveStaffEmail }: IParams) =>
      updateSmtpProvider(sender, host, username, port, password, secure, staffEmail, dealingEmail, sensitiveStaffEmail),
    {
      onError: (error: any) => {
        handleFormError(error);
      },
      onSuccess: () => {
        toast.success(_t('integrations.smtp-provider-configured'));
        onChange();
      },
    }
  );

  const setHostFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHost(e.target.value);
  };

  const setUsernameFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const setPortFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPort(e.target.value);
  };

  const setPasswordFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const setSenderFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailSender(e.target.value);
  };

  const setStaffFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStaffEmail(e.target.value);
  };

  const setSensitiveStaffFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSensitiveStaffEmail(e.target.value);
  };

  const setDealingFormValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDealingEmail(e.target.value);
  };

  const toggleSecure = () => {
    const secureConnectionEnabled = !secure;
    setSecure(secureConnectionEnabled);
  };

  const findError = (paramName: string) => {
    const error = findErrorFromValidation(updateSmtpProviderMutation.error, paramName);
    return error;
  };

  const callUpdateSmtpProviderMutation = () => {
    updateSmtpProviderMutation.mutate({
      host,
      port,
      username,
      password,
      sender: emailSender,
      secure,
      staffEmail,
      sensitiveStaffEmail: canManageSensitiveEmail ? sensitiveStaffEmail : undefined,
      dealingEmail,
    });
  };

  return (
    <CRow>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="host">{_t(`integrations.smtp-provider-host`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="host"
              name="host"
              value={host}
              onChange={setHostFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {hostQuery.error && <CLabel className="text-danger">{_t('integrations.smtp-provider-host-failed')}</CLabel>}
          {findError('host') && <CLabel className="text-danger">{findError('host')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="port">{_t(`integrations.smtp-provider-port`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="port"
              name="port"
              value={port}
              onChange={setPortFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {portQuery.error && <CLabel className="text-danger">{_t('integrations.smtp-provider-port-failed')}</CLabel>}
          {findError('port') && <CLabel className="text-danger">{findError('port')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="username">{_t(`integrations.smtp-provider-username`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={setUsernameFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {usernameQuery.error && (
            <CLabel className="text-danger">{_t('integrations.smtp-provider-username-failed')}</CLabel>
          )}
          {findError('username') && <CLabel className="text-danger">{findError('username')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="password">{_t(`integrations.smtp-provider-password`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="password"
              name="password"
              value={password}
              onChange={setPasswordFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {passwordQuery.error && (
            <CLabel className="text-danger">{_t('integrations.smtp-provider-password-failed')}</CLabel>
          )}
          {findError('password') && <CLabel className="text-danger">{findError('password')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="sender">{_t(`integrations.email-sender`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="sender"
              name="sender"
              value={emailSender}
              onChange={setSenderFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {emailSenderQuery.error && <CLabel className="text-danger">{_t('integrations.email-sender-failed')}</CLabel>}
          {findError('sender') && <CLabel className="text-danger">{findError('sender')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="sender">{_t(`integrations.staff-email`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="staffEmail"
              name="staffEmail"
              value={staffEmail}
              onChange={setStaffFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {staffEmailQuery.error && <CLabel className="text-danger">{_t('integrations.email-sender-failed')}</CLabel>}
          {findError('staffEmail') && <CLabel className="text-danger">{findError('staffEmail')}</CLabel>}
        </CFormGroup>
      </CCol>
      {canManageSensitiveEmail && (
        <CCol md={12}>
          <CFormGroup>
            <CLabel htmlFor="sensitiveStaffEmail">{_t(`integrations.sensitive-staff-email`)}</CLabel>
            <CInputGroup>
              <CInput
                type="text"
                id="sensitiveStaffEmail"
                name="sensitiveStaffEmail"
                value={sensitiveStaffEmail}
                onChange={setSensitiveStaffFormValue}
                className="instrument-input-height"
              />
            </CInputGroup>
            {sensitiveStaffEmailQuery.error && (
              <CLabel className="text-danger">{_t('integrations.sensitive-staff-email-failed')}</CLabel>
            )}
            {findError('sensitiveStaffEmail') && (
              <CLabel className="text-danger">{findError('sensitiveStaffEmail')}</CLabel>
            )}
          </CFormGroup>
        </CCol>
      )}
      <CCol md={12}>
        <CFormGroup>
          <div className="form-check form-check-inline">
            <CInputCheckbox
              className="form-check-input"
              type="checkbox"
              id="secure"
              onChange={toggleSecure}
              checked={secure}
            />
            <span>{_t('integrations.smtp-provider-secure')}</span>
          </div>
        </CFormGroup>
      </CCol>
      <CCol md={12}>
        <CFormGroup>
          <CLabel htmlFor="sender">{_t(`integrations.dealing-email`)}</CLabel>
          <CInputGroup>
            <CInput
              type="text"
              id="dealingEmail"
              name="dealingEmail"
              value={dealingEmail}
              onChange={setDealingFormValue}
              className="instrument-input-height"
            />
          </CInputGroup>
          {dealingEmailQuery.error && (
            <CLabel className="text-danger">{_t('integrations.dealing-email-failed')}</CLabel>
          )}
          {findError('dealingEmail') && <CLabel className="text-danger">{findError('dealingEmail')}</CLabel>}
        </CFormGroup>
      </CCol>
      <CCol md={12} className="d-flex justify-content-end">
        <ButtonWithLoader
          isLoading={updateSmtpProviderMutation.isLoading}
          onClick={callUpdateSmtpProviderMutation}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('action.save')}
          className="btn-md"
          showText={false}
          disabled={host === '' || emailSender === '' || port === '' || username === '' || password === ''}
        />
      </CCol>
    </CRow>
  );
};

export default SmtpProviderIntegration;
